import type { ComponentType } from 'react';

import { buildBlogCollectionRenderField } from '@/components/blocks/collection/field-renderers/blog';
import { buildCaseResultCollectionRenderField } from '@/components/blocks/collection/field-renderers/case-result';
import type { CollectionCardConfig, CollectionItem } from '@/lib/blocks/collection/blocks';
import type { IndexCollectionLayout } from '@/lib/blocks/collection/index-collection/layout';
import {
  readIndexPinnedCaseResultFieldOptions,
  resolveBlogIndexRenderField,
} from '@/lib/blocks/collection/index-collection/pinned-render';
import type { IndexCollectionContentType } from '@/lib/blocks/collection/index-collection/registry';
import type { PageBlock } from '@/types/block';
import type { RenderField } from '../../collection/types';
import { BlogIndexCollectionCard } from './blog-collection-card';
import { CaseResultIndexCollectionCard } from './case-result-collection-card';
import type { IndexCollectionCardShellProps } from './utils';

export type IndexCollectionCardComponentProps = IndexCollectionCardShellProps & {
  block: PageBlock;
  item: CollectionItem;
  cardConfig: CollectionCardConfig;
  layout: IndexCollectionLayout;
  renderField: RenderField;
  variantProps: Record<string, unknown>;
};

export type IndexRenderFieldFactory = (
  block: PageBlock,
  cardConfig: CollectionCardConfig,
  layout: IndexCollectionLayout,
) => (item: CollectionItem) => RenderField;

export type IndexCollectionContentTypeEntry = {
  Card: ComponentType<IndexCollectionCardComponentProps>;
  buildRenderFieldFactory: IndexRenderFieldFactory;
};

export const INDEX_COLLECTION_RENDERERS_BY_CONTENT_TYPE = new Map<
  IndexCollectionContentType,
  IndexCollectionContentTypeEntry
>([
  [
    'blog',
    {
      Card: BlogIndexCollectionCard,
      buildRenderFieldFactory: (block, cardConfig, layout) => {
        return (item) => {
          const { variant, pinned } = resolveBlogIndexRenderField({
            block,
            cardConfig,
            layout,
            item,
          });

          return buildBlogCollectionRenderField({
            item,
            cardConfig,
            variant,
            pinned,
          });
        };
      },
    },
  ],
  [
    'case_result',
    {
      Card: CaseResultIndexCollectionCard,
      buildRenderFieldFactory: (block, cardConfig, layout) => {
        const pinnedFieldOptions = readIndexPinnedCaseResultFieldOptions(block, layout);

        return (item) =>
          buildCaseResultCollectionRenderField({
            item,
            cardConfig,
            variant: pinnedFieldOptions ? 'pinned' : 'index',
            pinned: pinnedFieldOptions,
          });
      },
    },
  ],
]);
